import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronRight, 
  CheckCircle, 
  Loader2,
  User,
  Building,
  Phone,
  Mail,
  Globe,
  Instagram,
  Youtube,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScrollSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const GetProposalPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    phone: '',
    email: '',
    website: '',
    instagram: '',
    youtube: '',
    services_needed: [],
    goals: [],
    budget_range: '',
    timeline: '',
    city: '',
    industry: '',
    message: '',
  });

  const services = [
    { id: 'social_media', label: 'Social Media Management' },
    { id: 'podcast', label: 'Podcast Production' },
    { id: 'video_content', label: 'Video Content' },
    { id: 'ads', label: 'Ads & Performance Marketing' },
    { id: 'branding', label: 'Branding & Design' },
    { id: 'web', label: 'Website Development' },
  ];

  const goals = [
    { id: 'brand_awareness', label: 'Brand Awareness' },
    { id: 'leads', label: 'Generate Leads' },
    { id: 'sales', label: 'Increase Sales' },
    { id: 'community', label: 'Build Community' },
    { id: 'launch', label: 'Product/Service Launch' },
  ];

  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services_needed: prev.services_needed.includes(serviceId)
        ? prev.services_needed.filter((s) => s !== serviceId)
        : [...prev.services_needed, serviceId],
    }));
  };

  const handleGoalToggle = (goalId) => {
    setFormData((prev) => ({
      ...prev,
      goals: prev.goals.includes(goalId)
        ? prev.goals.filter((g) => g !== goalId)
        : [...prev.goals, goalId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/api/leads`, formData);
      setIsSuccess(true);
    } catch (error) {
      console.error('Failed to submit:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center py-32 px-6">
        <motion.div
          className="max-w-lg text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-syne font-bold text-white mb-4">
            Thank you!
          </h1>
          <p className="text-fp-textMuted mb-8">
            We've received your proposal request. Our team will review your requirements 
            and get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/917030771071?text=Hi, I just submitted a proposal request from the website. Name: ${formData.full_name}, Company: ${formData.company}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="success-whatsapp-btn"
            >
              <MessageSquare className="w-5 h-5" />
              WhatsApp Us
            </a>
            <a
              href="https://calendly.com/focuspixel/new-meeting"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="success-book-call-btn"
            >
              Book a Call
            </a>
          </div>
          <Link
            to="/"
            className="inline-block mt-8 text-fp-textMuted hover:text-white text-sm"
          >
            ← Back to Home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-32">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 mb-16">
        <ScrollSection>
          <p className="text-primary font-mono text-sm tracking-widest mb-4">GET A PROPOSAL</p>
          <h1 className="text-4xl md:text-5xl font-syne font-bold text-white mb-6">
            Let's build something<br />incredible together
          </h1>
          <p className="text-fp-textMuted text-lg max-w-2xl">
            Tell us about your project and goals. We'll craft a custom proposal 
            tailored to your needs.
          </p>
        </ScrollSection>
      </section>

      {/* Form */}
      <section className="max-w-4xl mx-auto px-6">
        <form onSubmit={handleSubmit} className="space-y-12" data-testid="proposal-form">
          {/* Contact Information */}
          <ScrollSection delay={0.1}>
            <div className="glass rounded-sm p-8">
              <h2 className="text-xl font-syne font-bold text-white mb-6">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="full_name" className="text-fp-textMuted">Full Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-fp-textMuted" />
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Your name"
                      className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      required
                      data-testid="input-full-name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company" className="text-fp-textMuted">Company / Brand</Label>
                  <div className="relative mt-1">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-fp-textMuted" />
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Company name"
                      className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-fp-textMuted">Phone Number *</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-fp-textMuted" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                      className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-fp-textMuted">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-fp-textMuted" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@example.com"
                      className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="website" className="text-fp-textMuted">Website</Label>
                  <div className="relative mt-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-fp-textMuted" />
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://yoursite.com"
                      className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="input-website"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="instagram" className="text-fp-textMuted">Instagram</Label>
                  <div className="relative mt-1">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-fp-textMuted" />
                    <Input
                      id="instagram"
                      value={formData.instagram}
                      onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                      placeholder="@yourbrand"
                      className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="input-instagram"
                    />
                  </div>
                </div>
              </div>
            </div>
          </ScrollSection>

          {/* Services & Goals */}
          <ScrollSection delay={0.2}>
            <div className="glass rounded-sm p-8">
              <h2 className="text-xl font-syne font-bold text-white mb-6">Services & Goals</h2>
              
              <div className="mb-8">
                <Label className="text-fp-textMuted mb-3 block">Services Needed</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer transition-all ${
                        formData.services_needed.includes(service.id)
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`service-${service.id}`}
                    >
                      <Checkbox
                        checked={formData.services_needed.includes(service.id)}
                        onCheckedChange={() => handleServiceToggle(service.id)}
                        className="border-white/20"
                      />
                      <span className="text-sm text-white">{service.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-fp-textMuted mb-3 block">Project Goals</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {goals.map((goal) => (
                    <label
                      key={goal.id}
                      className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer transition-all ${
                        formData.goals.includes(goal.id)
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`goal-${goal.id}`}
                    >
                      <Checkbox
                        checked={formData.goals.includes(goal.id)}
                        onCheckedChange={() => handleGoalToggle(goal.id)}
                        className="border-white/20"
                      />
                      <span className="text-sm text-white">{goal.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </ScrollSection>

          {/* Budget & Timeline */}
          <ScrollSection delay={0.3}>
            <div className="glass rounded-sm p-8">
              <h2 className="text-xl font-syne font-bold text-white mb-6">Budget & Timeline</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="budget" className="text-fp-textMuted">Budget Range</Label>
                  <Select
                    value={formData.budget_range}
                    onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                  >
                    <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white" data-testid="select-budget">
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent className="bg-fp-surface border-white/10">
                      <SelectItem value="low">Starting / Low Budget</SelectItem>
                      <SelectItem value="medium">Medium Budget</SelectItem>
                      <SelectItem value="high">High / Premium Budget</SelectItem>
                      <SelectItem value="not_sure">Not Sure Yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="timeline" className="text-fp-textMuted">Timeline</Label>
                  <Select
                    value={formData.timeline}
                    onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                  >
                    <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white" data-testid="select-timeline">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent className="bg-fp-surface border-white/10">
                      <SelectItem value="asap">ASAP / Urgent</SelectItem>
                      <SelectItem value="2-4_weeks">2-4 Weeks</SelectItem>
                      <SelectItem value="1-3_months">1-3 Months</SelectItem>
                      <SelectItem value="exploring">Just Exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="city" className="text-fp-textMuted">City / Region</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Pune, Mumbai"
                    className="mt-1 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-city"
                  />
                </div>

                <div>
                  <Label htmlFor="industry" className="text-fp-textMuted">Industry / Niche</Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    placeholder="e.g., Education, Healthcare"
                    className="mt-1 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-industry"
                  />
                </div>
              </div>
            </div>
          </ScrollSection>

          {/* Message */}
          <ScrollSection delay={0.4}>
            <div className="glass rounded-sm p-8">
              <h2 className="text-xl font-syne font-bold text-white mb-6">Tell Us More</h2>
              <div>
                <Label htmlFor="message" className="text-fp-textMuted">Project Brief / Message</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe your project, goals, challenges, or any specific requirements..."
                  className="mt-1 min-h-[150px] bg-black/50 border-white/10 text-white placeholder:text-white/30"
                  data-testid="input-message"
                />
              </div>
            </div>
          </ScrollSection>

          {/* Submit */}
          <ScrollSection delay={0.5}>
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <p className="text-fp-textMuted text-sm">
                By submitting, you agree to our privacy policy.
              </p>
              <Button
                type="submit"
                className="btn-primary px-12"
                disabled={isLoading}
                data-testid="submit-proposal-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Proposal Request
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </ScrollSection>
        </form>
      </section>
    </div>
  );
};

export default GetProposalPage;
